import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as PhosphorIcons from '@phosphor-icons/react';
import { ChevronDown, Sparkles, ArrowRight, X } from 'lucide-react';
import { TOOLS } from '../lib/tools';

// Curated mega-menu categories (collapse internal taxonomy into 5 user-friendly groups)
const MENU_GROUPS = [
  {
    key: 'pdf',
    label: 'PDF Tools',
    description: 'Organize, edit & secure',
    slugs: ['merge-pdf', 'split-pdf', 'rotate-pdf', 'delete-pages', 'extract-pages', 'reorder-pages', 'add-watermark', 'add-page-numbers', 'protect-pdf', 'unlock-pdf', 'sign-pdf'],
  },
  {
    key: 'convert',
    label: 'Document Conversion',
    description: 'Word, Excel, PPT, HTML ↔ PDF',
    slugs: ['word-to-pdf', 'pdf-to-word', 'excel-to-pdf', 'pdf-to-excel', 'powerpoint-to-pdf', 'pdf-to-powerpoint', 'html-to-pdf', 'text-to-pdf', 'pdf-to-text'],
  },
  {
    key: 'image',
    label: 'Image Tools',
    description: 'JPG, PNG ↔ PDF',
    slugs: ['jpg-to-pdf', 'pdf-to-jpg', 'pdf-to-png'],
  },
  {
    key: 'compression',
    label: 'Compression & Enhance',
    description: 'Shrink, OCR, repair, archive',
    slugs: ['compress-pdf', 'ocr-pdf', 'repair-pdf', 'grayscale-pdf', 'pdf-to-pdfa', 'flatten-pdf', 'crop-pdf'],
  },
  {
    key: 'ai',
    label: 'AI Tools',
    description: 'Powered by Gemini',
    badge: 'NEW',
    slugs: ['ai-summarize', 'ai-chat', 'ai-translate', 'ai-extract'],
  },
];

function ToolIcon({ name, color, size = 18 }) {
  const Icon = PhosphorIcons[name] || PhosphorIcons.File;
  return <Icon size={size} weight="duotone" color={color} />;
}

function ToolLink({ slug, onClick }) {
  const tool = TOOLS.find((t) => t.slug === slug);
  if (!tool) return null;
  return (
    <Link
      to={`/${slug}`}
      onClick={onClick}
      data-testid={`mega-tool-${slug}`}
      className="flex items-center gap-2.5 p-2 rounded-md hover:bg-white/5 transition group"
    >
      <span
        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: `${tool.color}18`, border: `1px solid ${tool.color}33` }}
      >
        <ToolIcon name={tool.icon} color={tool.color} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-white font-medium group-hover:text-vault-gold transition truncate">
          {tool.name}
        </div>
      </div>
    </Link>
  );
}

export default function MegaMenu() {
  const [open, setOpen] = React.useState(false);
  const [mobileExpanded, setMobileExpanded] = React.useState(null);
  const containerRef = React.useRef(null);
  const closeTimer = React.useRef(null);
  const location = useLocation();

  // Close on route change
  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  // Hover handlers with grace period
  const handleEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        data-testid="mega-menu-trigger"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1 text-sm transition ${open ? 'text-vault-gold' : 'text-gray-300 hover:text-vault-gold'}`}
        aria-expanded={open}
        aria-haspopup="true"
      >
        All Tools
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Desktop mega panel */}
      <div
        data-testid="mega-menu-panel"
        className={`hidden md:block absolute top-full left-1/2 -translate-x-1/2 mt-3 transition-all duration-200 ${
          open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'
        }`}
        style={{ width: 'min(960px, 92vw)' }}
      >
        {/* Invisible spacer to prevent gap-flicker */}
        <div className="absolute -top-3 inset-x-0 h-3" />
        <div className="glass-panel rounded-2xl p-6 shadow-2xl border border-white/10">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-5">
            {MENU_GROUPS.map((group) => (
              <div key={group.key} data-testid={`mega-group-${group.key}`}>
                <div className="flex items-center gap-2 mb-3">
                  <h4 className="font-heading font-semibold text-sm text-white">{group.label}</h4>
                  {group.badge && (
                    <span className="text-[9px] tracking-wider font-mono text-vault-gold border border-vault-gold/40 rounded px-1 py-0.5">
                      {group.badge}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mb-3">{group.description}</p>
                <div className="space-y-0.5">
                  {group.slugs.slice(0, 6).map((s) => (
                    <ToolLink key={s} slug={s} onClick={() => setOpen(false)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between">
            <p className="text-xs text-gray-500">
              <Sparkles className="w-3 h-3 inline text-vault-gold mr-1" />
              37 tools · Free · No watermarks
            </p>
            <Link
              data-testid="mega-view-all"
              to="/#tools"
              onClick={() => setOpen(false)}
              className="text-xs text-vault-gold hover:text-vault-gold-hover inline-flex items-center gap-1 font-medium"
            >
              Browse all 37 tools <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// Separate export for mobile accordion variant (used inside mobile menu)
export function MegaMenuMobile({ onNavigate }) {
  const [expanded, setExpanded] = React.useState(null);
  return (
    <div data-testid="mega-menu-mobile" className="space-y-1">
      {MENU_GROUPS.map((group) => (
        <div key={group.key}>
          <button
            data-testid={`mega-mobile-toggle-${group.key}`}
            onClick={() => setExpanded(expanded === group.key ? null : group.key)}
            className="flex items-center justify-between w-full p-3 rounded-lg bg-vault-paper/40 border border-white/5 hover:border-vault-gold/30 transition"
          >
            <div className="flex items-center gap-2">
              <span className="font-medium text-white text-sm">{group.label}</span>
              {group.badge && (
                <span className="text-[9px] tracking-wider font-mono text-vault-gold border border-vault-gold/40 rounded px-1 py-0.5">
                  {group.badge}
                </span>
              )}
            </div>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expanded === group.key ? 'rotate-180' : ''}`} />
          </button>
          {expanded === group.key && (
            <div className="mt-1 pl-3 pr-1 py-2 space-y-0.5 animate-fade-up">
              {group.slugs.map((s) => (
                <ToolLink key={s} slug={s} onClick={onNavigate} />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
